import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Plus, Users } from 'lucide-react'
import { useStore } from '@tanstack/react-store'

import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { useInfiniteContactsList } from '@/stores/contacts.store'
import { organizationStore } from '@/stores/organization.store'
import { ContactsTable } from '@/components/contacts/contacts-table'
import { ContactSheet } from '@/components/contacts/contact-edit-sheet'
import { mapContactToDisplay } from '@/components/contacts/types'
import { Empty, EmptyMedia, EmptyDescription } from '@/components/ui/empty'
import { Button } from '@/components/ui/button'
import { LoadMoreBar } from '@/components/shared/load-more-bar'
import { ActionGate } from '@/components/shared/action-gate'

export const Route = createFileRoute('/_app/_crm/contacts/')({
  component: ContactsPage,
})

function ContactsPage() {
  useBreadcrumbFromRoute()

  const [createSheetOpen, setCreateSheetOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)
  // Debounce the query so we hit the server as the user pauses typing, not on
  // every keystroke.
  const debouncedSearch = useDebouncedValue(search.trim(), 300)
  const selectedOrganizationId = useStore(organizationStore, (s) => s.selectedOrganizationId)
  const {
    contacts,
    totalCount,
    hasMore,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    error,
    hasLoaded,
  } = useInfiniteContactsList(undefined, { search: debouncedSearch, includeArchived })

  // Map proto contacts to display format
  const displayContacts = contacts.map(mapContactToDisplay)

  const isSearchLoading =
    search.trim() !== debouncedSearch ||
    isLoading ||
    !hasLoaded ||
    (isFetching && isPlaceholderData)

  if (isError) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="min-h-[400px]">
          <EmptyMedia variant="icon">
            <Users className="h-10 w-10" />
          </EmptyMedia>
          <EmptyDescription>
            Error loading contacts: {error?.message || 'Unknown error'}
          </EmptyDescription>
        </Empty>
      </div>
    )
  }

  // Only the very first load replaces the page; later loads keep the table (and
  // its search box) mounted so typing never drops focus.
  if (!hasLoaded) {
    return (
      <div className="flex h-full flex-col gap-4 p-4">
        <Empty className="min-h-[400px]">
          <EmptyMedia variant="icon">
            <Users className="h-10 w-10" />
          </EmptyMedia>
          <EmptyDescription>
            Loading contacts...
          </EmptyDescription>
        </Empty>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="rounded-lg border border-status-info-border bg-status-info-bg p-4 text-sm text-status-info-fg">
        <strong>CRM features are coming soon.</strong>{' '}
        Use this page to find and look up contacts. Full CRM capabilities are in active development.
      </div>

      <div className="flex items-center justify-end">
        <ActionGate
          organizationId={selectedOrganizationId || ''}
          permission="ContactsManage"
          verb="create"
          noun="contacts"
        >
          <Button onClick={() => setCreateSheetOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Contact
          </Button>
        </ActionGate>
      </div>

      <ContactsTable
        data={displayContacts}
        search={search}
        onSearchChange={setSearch}
        isLoading={isSearchLoading}
        includeArchived={includeArchived}
        onIncludeArchivedChange={setIncludeArchived}
      />

      <LoadMoreBar
        loaded={contacts.length}
        total={totalCount}
        hasMore={hasMore}
        isLoading={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
      />

      <ContactSheet
        organizationId={selectedOrganizationId || ''}
        contact={null}
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
      />
    </div>
  )
}

/** Returns `value` after it has stopped changing for `delayMs` milliseconds. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delayMs)
    return () => clearTimeout(timeoutId)
  }, [delayMs, value])

  return debouncedValue
}
